import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axiosInstance from '../../../api/axiosConfig';
import {
  HiOutlinePlus, HiOutlineTrash, HiOutlineChevronDown, HiOutlineChevronUp,
  HiOutlineMicrophone, HiOutlineStop, HiOutlineSparkles, HiOutlinePencil
} from 'react-icons/hi';
import AssigneePicker from './AssigneePicker';
import AttachmentList from './AttachmentList';
import { hasCardAction } from '../../../utils/permissionHelper';

const OFF_AGENDA = 'Off-agenda';

const blankTask = () => ({ title: '', deadline: '', assignee: [] });
const blankActionItem = () => ({
  title: '', description: '', deadline: '', priority: 'Medium',
  responsible: [],
  tasks: []
});
const blankDiscussion = (agendaName = OFF_AGENDA, agendaRef = null) => ({
  text: '', agendaName, agendaRef, actionItems: []
});

const sameAgenda = (a, b) => (a || '').trim().toLowerCase() === (b || '').trim().toLowerCase();

// AI transcription sometimes returns placeholder dates like "2024-XX-20" when the
// speaker named a day but not a month. Accept only real YYYY-MM-DD strings.
const toISODate = (v) => {
  const s = v ? String(v).slice(0, 10) : '';
  return /^\d{4}-\d{2}-\d{2}$/.test(s) && !isNaN(Date.parse(s)) ? s : '';
};

// MediaRecorder writes WebM without a duration in the EBML header, so
// `audio.duration` reads as `Infinity` until the browser scans the whole file.
// Seeking past the end forces the scan; we then reset the cursor to 0.
const getRealAudioDuration = (url) => new Promise((resolve) => {
  const audio = new Audio();
  audio.preload = 'metadata';
  let done = false;
  const finish = (d) => {
    if (done) return;
    done = true;
    try { audio.src = ''; } catch (_) {}
    resolve(Number.isFinite(d) && d > 0 ? d : 0);
  };
  audio.addEventListener('error', () => finish(0));
  audio.addEventListener('loadedmetadata', () => {
    if (Number.isFinite(audio.duration) && audio.duration > 0) {
      finish(audio.duration);
      return;
    }
    const onTimeUpdate = () => {
      audio.removeEventListener('timeupdate', onTimeUpdate);
      const d = audio.duration;
      try { audio.currentTime = 0; } catch (_) {}
      finish(d);
    };
    audio.addEventListener('timeupdate', onTimeUpdate);
    try { audio.currentTime = 1e9; } catch (_) { finish(0); }
  });
  audio.src = url;
});

// Apply the same Infinity-duration fix to the visible <audio controls> element
// so the scrubber and total time render correctly for MediaRecorder webm files.
const fixAudioElementDuration = (el) => {
  if (!el || Number.isFinite(el.duration) && el.duration > 0) return;
  const onTimeUpdate = () => {
    el.removeEventListener('timeupdate', onTimeUpdate);
    try { el.currentTime = 0; } catch (_) {}
  };
  el.addEventListener('timeupdate', onTimeUpdate);
  try { el.currentTime = 1e9; } catch (_) {}
};

const MeetingMinutes = ({ missions, systemUsers = [], lockState = 'active', onMissionUpdate, user }) => {
  const canEdit = user?.role === 'system_admin' || hasCardAction(user?.permissions, 'vision', 'edit');
  const [minutes, setMinutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [filterMission, setFilterMission] = useState('');
  const [filterGoal, setFilterGoal] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [editingMinute, setEditingMinute] = useState(null);

  const [goalsByMission, setGoalsByMission] = useState({});
  const [agendas, setAgendas] = useState([]);
  const [newAgendaName, setNewAgendaName] = useState('');

  const [form, setForm] = useState({
    mission: '',
    goal: '',
    meetingDate: new Date().toISOString().split('T')[0],
    topic: '',
    selectedAgendas: [],          // [{ agendaId, name }]
    participants: [],             // [{ userId } | { externalName }]
    discussionPoints: [],
    notes: '',
    nextMeetingDate: ''
  });

  // ─── Audio recording ─────────────────────────────────────────────────────
  const [recordingState, setRecordingState] = useState('idle');
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [pendingChunkUploads, setPendingChunkUploads] = useState(0);
  const [chunkUploadWarning, setChunkUploadWarning] = useState('');
  // null = no upload in flight; 0-100 = uploaded-file transfer progress.
  const [audioUploadPct, setAudioUploadPct] = useState(null);
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine
  );
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const fileInputRef = useRef(null);
  // Serializes chunk uploads so server-side appendFile lands them in order.
  const chunkUploadQueueRef = useRef(Promise.resolve());
  // When user hits Discard before onstop fires, suppress the late draft save.
  const discardedRef = useRef(false);
  // Wall-clock start time → timer survives background-tab throttling.
  const recordingStartTimeRef = useRef(null);
  // Draft id captured at recording start so chunk uploads don't race React state.
  const recordingDraftIdRef = useRef(null);
  // Counts chunks that permanently failed to upload (after retries). If > 0 when
  // recording ends, we re-upload the complete local blob so the server copy is
  // not left with a gap.
  const failedChunkCountRef = useRef(0);
  // Screen Wake Lock held while recording so the display (and therefore the mic)
  // does not sleep during a long meeting.
  const wakeLockRef = useRef(null);
  const pendingChunkUploadsRef = useRef(0);
  const lastDraftSnapshotRef = useRef('');

  // ─── Drafts (autosave + resume) ──────────────────────────────────────────
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [myDrafts, setMyDrafts] = useState([]);
  const [savingDraft, setSavingDraft] = useState(false);
  const [resumingDraftId, setResumingDraftId] = useState(null);
  // Set when recording stops for any reason other than the user pressing Stop.
  const [recordingError, setRecordingError] = useState(null);

  const recordingSupport = useMemo(() => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return { supported: false, reason: 'Recording is not available in this environment.' };
    }
    const isLocalhost = ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname);
    if (!window.isSecureContext && !isLocalhost) {
      return { supported: false, reason: 'Recording requires HTTPS or localhost.' };
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      return { supported: false, reason: 'This browser does not expose microphone access.' };
    }
    if (typeof window.MediaRecorder === 'undefined') {
      return { supported: false, reason: 'This browser does not support MediaRecorder audio recording.' };
    }
    return { supported: true, reason: '' };
  }, []);

  const isInstalledApp = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return Boolean(
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator?.standalone
    );
  }, []);

  const formStateForDraft = useCallback(() => ({
    mission: form.mission || null,
    goal: form.goal || null,
    meetingDate: form.meetingDate || '',
    topic: form.topic || '',
    agendas: form.selectedAgendas || [],
    participants: form.participants || [],
    discussionPoints: form.discussionPoints || [],
    notes: form.notes || '',
    nextMeetingDate: form.nextMeetingDate || ''
  }), [form]);

  const loadMyDrafts = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get('/api/vision/meeting-drafts');
      setMyDrafts(data.data || []);
    } catch (err) { console.error('loadMyDrafts:', err); }
  }, []);

  useEffect(() => { loadMyDrafts(); }, [loadMyDrafts]);

  // Hold a Screen Wake Lock while recording so the OS does not dim/sleep the
  // display — display sleep is a common trigger for the mic track ending mid
  // meeting, especially in the installed (standalone) PWA. Best-effort: silently
  // no-ops on browsers without the API or on insecure (non-HTTPS) contexts.
  const acquireWakeLock = useCallback(async () => {
    try {
      if ('wakeLock' in navigator && !wakeLockRef.current) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        // The lock is auto-released when the tab is hidden; drop our ref so the
        // visibilitychange handler knows to re-acquire it on return.
        wakeLockRef.current.addEventListener?.('release', () => { wakeLockRef.current = null; });
      }
    } catch (err) {
      console.warn('[wakeLock] could not acquire:', err?.message || err);
    }
  }, []);

  const releaseWakeLock = useCallback(async () => {
    try { await wakeLockRef.current?.release?.(); } catch (_) {}
    wakeLockRef.current = null;
  }, []);

  const flushRecorderBuffer = useCallback(() => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'recording' || typeof recorder.requestData !== 'function') return false;
    try {
      recorder.requestData();
      return true;
    } catch (_) {
      return false;
    }
  }, []);

  const adjustPendingChunkUploads = useCallback((delta) => {
    pendingChunkUploadsRef.current = Math.max(0, pendingChunkUploadsRef.current + delta);
    setPendingChunkUploads(pendingChunkUploadsRef.current);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const updateOnlineStatus = () => setIsOnline(navigator.onLine);
    window.addEventListener('online', updateOnlineStatus);
    window.addEventListener('offline', updateOnlineStatus);
    return () => {
      window.removeEventListener('online', updateOnlineStatus);
      window.removeEventListener('offline', updateOnlineStatus);
    };
  }, []);

  // When the tab goes hidden (minimised, alt-tabbed, etc.) flush whatever the
  // MediaRecorder has buffered so far.  Without this, up to one full timeslice
  // of audio (3 s) can be lost if the browser throttles or kills the tab while
  // it is in the background.  On return, re-acquire the wake lock (the browser
  // auto-releases it whenever the document is hidden).
  useEffect(() => {
    const onVisibilityChange = () => {
      if (mediaRecorderRef.current?.state !== 'recording') return;
      if (document.hidden) {
        flushRecorderBuffer();
      } else if (!wakeLockRef.current) {
        acquireWakeLock();
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [acquireWakeLock, flushRecorderBuffer]);

  // Browser/PWA lifecycle events can fire without a normal React unmount. Flush
  // the recorder buffer so the last slice has the best chance to reach the draft
  // before Chrome freezes, navigates away, or closes a standalone app window.
  useEffect(() => {
    const flushForLifecycle = () => { flushRecorderBuffer(); };
    window.addEventListener('pagehide', flushForLifecycle);
    window.addEventListener('blur', flushForLifecycle);
    document.addEventListener('freeze', flushForLifecycle);
    return () => {
      window.removeEventListener('pagehide', flushForLifecycle);
      window.removeEventListener('blur', flushForLifecycle);
      document.removeEventListener('freeze', flushForLifecycle);
    };
  }, [flushRecorderBuffer]);

  // Warn the user before leaving (tab close, refresh, or closing the installed
  // PWA window) while a recording is still in progress — otherwise the in-flight
  // audio buffer is lost without warning.
  useEffect(() => {
    const onBeforeUnload = (e) => {
      if (mediaRecorderRef.current?.state === 'recording' || pendingChunkUploadsRef.current > 0) {
        flushRecorderBuffer();
        e.preventDefault();
        e.returnValue = ''; // required for Chrome to show the native prompt
        return '';
      }
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [flushRecorderBuffer]);

  // If this component unmounts (e.g. the user switches to another card) while a
  // recording is live, flush the last slice to the server, release the mic +
  // wake lock, and clear the timer so nothing leaks.  The streamed chunks are
  // already on the server, so the draft remains fully resumable.
  useEffect(() => {
    return () => {
      try {
        const rec = mediaRecorderRef.current;
        if (rec && rec.state !== 'inactive') {
          flushRecorderBuffer();
          rec.stop();
        }
      } catch (_) {}
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
      releaseWakeLock();
    };
  }, [flushRecorderBuffer, releaseWakeLock]);

  // Create a draft on the server using the current audio blob + form state.
  // Returns the new draftId (or null on failure).
  const createDraftWithAudio = useCallback(async (blob, { onProgress } = {}) => {
    try {
      const fd = new FormData();
      if (blob) {
        const ext = blob.type.includes('webm') ? 'webm' : blob.type.includes('mp4') ? 'mp4' : 'wav';
        fd.append('audio', blob, `meeting.${ext}`);
      }
      const s = formStateForDraft();
      Object.entries(s).forEach(([k, v]) => {
        if (Array.isArray(v) || (v && typeof v === 'object')) fd.append(k, JSON.stringify(v));
        else if (v !== null && v !== undefined && v !== '') fd.append(k, v);
      });
      fd.append('stage', blob ? 'recording' : 'editing');
      const { data } = await axiosInstance.post('/api/vision/meeting-drafts', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
          ? (ev) => { if (ev.total) onProgress(Math.round((ev.loaded / ev.total) * 100)); }
          : undefined
      });
      setCurrentDraftId(data.data._id);
      recordingDraftIdRef.current = data.data._id;
      loadMyDrafts();
      return data.data._id;
    } catch (err) {
      console.error('createDraftWithAudio:', err);
      alert('Failed to save draft on server. Your recording is still in this tab — try Save Draft again.');
      return null;
    }
  }, [formStateForDraft, loadMyDrafts]);

  // PUT the current form into an existing draft (no audio change).
  const persistDraft = useCallback(async ({ silent = false } = {}) => {
    if (!currentDraftId) return null;
    if (!silent) setSavingDraft(true);
    try {
      const { data } = await axiosInstance.put(
        `/api/vision/meeting-drafts/${currentDraftId}`,
        formStateForDraft()
      );
      if (!silent) loadMyDrafts();
      return data.data;
    } catch (err) {
      console.error('persistDraft:', err);
      if (!silent) alert('Failed to save draft. Your work is still in this tab.');
      return null;
    } finally {
      if (!silent) setSavingDraft(false);
    }
  }, [currentDraftId, formStateForDraft, loadMyDrafts]);

  useEffect(() => {
    if (!currentDraftId || recordingState === 'transcribing') return undefined;
    const snapshot = JSON.stringify(formStateForDraft());
    const snapshotKey = `${currentDraftId}:${snapshot}`;
    if (lastDraftSnapshotRef.current === snapshotKey) return undefined;

    const timeout = setTimeout(async () => {
      const saved = await persistDraft({ silent: true });
      if (saved) lastDraftSnapshotRef.current = snapshotKey;
    }, 1500);

    return () => clearTimeout(timeout);
  }, [currentDraftId, formStateForDraft, persistDraft, recordingState]);

  const handleFileUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('audio/') && !/\.(mp3|wav|m4a|ogg|webm|aac|flac|opus)$/i.test(file.name)) {
      alert('Please select an audio file');
      return;
    }
    if (file.size > 500 * 1024 * 1024) { alert('File too large. Max 500 MB.'); return; }
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(file);
    const url = URL.createObjectURL(file);
    setAudioUrl(url);
    setUploadedFileName(file.name);
    setRecordingState('recorded');
    setTranscript('');

    // Get actual duration (handles webm Infinity case via seek-trick).
    getRealAudioDuration(url).then((d) => {
      if (d > 0) setRecordingSeconds(Math.floor(d));
    });

    if (fileInputRef.current) fileInputRef.current.value = '';
    // Push audio to server immediately so we don't lose it if the tab closes.
    // Progress state locks Transcribe/Discard/Re-record until the server copy exists.
    setAudioUploadPct(0);
    try {
      await createDraftWithAudio(file, { onProgress: setAudioUploadPct });
    } finally {
      setAudioUploadPct(null);
    }
  };

  const formatDuration = (s) => {
    const total = Math.max(0, Math.floor(s));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const sec = total % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
  };

  // Open a recording session on the server BEFORE we start MediaRecorder, so we
  // have a draftId to stream chunks to. Returns the draftId or null on failure.
  const openRecordingSession = useCallback(async (audioMimeType) => {
    try {
      const payload = { ...formStateForDraft(), audioMimeType };
      const { data } = await axiosInstance.post('/api/vision/meeting-drafts/start-recording', payload);
      const id = data?.data?._id;
      if (id) {
        setCurrentDraftId(id);
        recordingDraftIdRef.current = id;
        loadMyDrafts();
      }
      return id || null;
    } catch (err) {
      console.error('openRecordingSession:', err);
      return null;
    }
  }, [formStateForDraft, loadMyDrafts]);

  // Serialized chunk upload with bounded retry. A failed chunk is logged and
  // skipped after retries — the queue stays alive so subsequent chunks still
  // land in order rather than the whole stream collapsing on one bad packet.
  const queueChunkUpload = (draftId, chunk) => {
    adjustPendingChunkUploads(1);
    const task = async () => {
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const fd = new FormData();
          fd.append('chunk', chunk, 'chunk.webm');
          await axiosInstance.post(
            `/api/vision/meeting-drafts/${draftId}/audio-chunk`,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 60_000 }
          );
          setChunkUploadWarning('');
          return;
        } catch (err) {
          if (attempt === 3) {
            console.error(`[chunk upload] giving up after ${attempt} attempts:`, err.message || err);
            // Mark integrity gap → finalize() will re-upload the full local blob.
            failedChunkCountRef.current += 1;
            setChunkUploadWarning(
              'Connection issue while saving audio. Keep this window open; stopping the recording will re-save the complete local audio.'
            );
            return;
          }
          await new Promise(r => setTimeout(r, 800 * attempt));
        }
      }
    };
    const wrappedTask = () => task().finally(() => adjustPendingChunkUploads(-1));
    chunkUploadQueueRef.current = chunkUploadQueueRef.current.then(wrappedTask, wrappedTask);
    return chunkUploadQueueRef.current;
  };

  const startRecording = async () => {
    if (!recordingSupport.supported) {
      setRecordingError(recordingSupport.reason);
      return;
    }
    if (mediaRecorderRef.current?.state === 'recording') return;

    setRecordingError(null); // clear any previous interruption banner
    setChunkUploadWarning('');
    setPendingChunkUploads(0);
    pendingChunkUploadsRef.current = 0;
    setTranscript('');
    setUploadedFileName('');
    setAudioBlob(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }

    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const RecorderCtor = window.MediaRecorder;
      const preferred = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
      const mimeType = preferred.find(m =>
        typeof RecorderCtor.isTypeSupported === 'function' && RecorderCtor.isTypeSupported(m)
      ) || '';
      const recorder = mimeType ? new RecorderCtor(stream, { mimeType }) : new RecorderCtor(stream);
      const effectiveMime = recorder.mimeType || mimeType || 'audio/webm';

      // 1) Open the server-side session FIRST so chunks land in a real file from chunk #1.
      const draftId = await openRecordingSession(effectiveMime);
      if (!draftId) {
        stream.getTracks().forEach(t => t.stop());
        alert('Could not start recording session on the server. Check your connection and try again.');
        return;
      }

      audioChunksRef.current = [];
      discardedRef.current = false;
      chunkUploadQueueRef.current = Promise.resolve();
      failedChunkCountRef.current = 0;

      // ─── Shared finalization ────────────────────────────────────────────────
      // All stop paths (normal stop, onerror, track.onended, watchdog) funnel
      // through here.  The hasFinalized guard ensures it runs exactly once even
      // if multiple paths fire at the same time (e.g. onerror + onstop).
      const hasFinalized = { current: false };

      const finalize = () => {
        if (hasFinalized.current) return;
        hasFinalized.current = true;

        stream.getTracks().forEach(t => t.stop());
        if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
        recordingStartTimeRef.current = null;
        releaseWakeLock();
        if (mediaRecorderRef.current === recorder) mediaRecorderRef.current = null;

        if (discardedRef.current) {
          // Discard requested mid-stop: wipe local + remote state.
          chunkUploadQueueRef.current.catch(() => {}).then(async () => {
            try { await axiosInstance.delete(`/api/vision/meeting-drafts/${draftId}`); } catch (_) {}
            audioChunksRef.current = [];
            if (recordingDraftIdRef.current === draftId) recordingDraftIdRef.current = null;
            loadMyDrafts();
          });
          return;
        }

        // Build local blob for instant playback.
        const blob = new Blob(audioChunksRef.current, { type: effectiveMime });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        setUploadedFileName('');

        // Resolve real duration (handles webm Infinity via seek-trick).
        getRealAudioDuration(url).then((d) => {
          if (d > 0) setRecordingSeconds(Math.floor(d));
        });

        setRecordingState('recorded');

        // Drain in-flight uploads, then finalize the draft. Normally every chunk
        // already streamed to the server during recording, so we do NOT re-upload.
        // BUT if any chunk permanently failed (network blip beyond retries), the
        // server file has a gap — re-upload the complete local blob, which the
        // backend uses to REPLACE the gapped file, so nothing is lost.
        chunkUploadQueueRef.current.catch(() => {}).then(async () => {
          try {
            if (failedChunkCountRef.current > 0 && blob.size > 0) {
              console.warn(`[recording] ${failedChunkCountRef.current} chunk(s) failed mid-stream — re-uploading full blob`);
              const ext = effectiveMime.includes('webm') ? 'webm' : effectiveMime.includes('mp4') ? 'mp4' : 'wav';
              const fd = new FormData();
              fd.append('audio', blob, `meeting.${ext}`);
              await axiosInstance.post(`/api/vision/meeting-drafts/${draftId}/audio`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
              });
            }
            await axiosInstance.put(`/api/vision/meeting-drafts/${draftId}`, { stage: 'editing' });
            setChunkUploadWarning('');
            pendingChunkUploadsRef.current = 0;
            setPendingChunkUploads(0);
          } catch (err) { console.error('finalize recording stage:', err); }
          loadMyDrafts();
        });
      };
      // ────────────────────────────────────────────────────────────────────────

      recorder.ondataavailable = (e) => {
        if (!e.data || e.data.size === 0) return;
        audioChunksRef.current.push(e.data);   // keep locally for instant playback
        queueChunkUpload(draftId, e.data);     // and stream to disk on the server
      };

      // Normal stop path.
      recorder.onstop = finalize;

      // ── Failure path 1: MediaRecorder device / encoding error ──────────────
      // Chrome fires onerror and moves the recorder to 'inactive' WITHOUT
      // firing onstop, so without this handler the UI freezes on the timer.
      recorder.onerror = (event) => {
        const name = event.error?.name || 'DeviceError';
        console.error('[MediaRecorder] onerror:', name, event.error ?? event);
        setRecordingError(
          `Recording interrupted (${name}). The audio captured so far has been preserved — you can still transcribe it.`
        );
        finalize();
      };

      // ── Failure path 2: browser pauses recorder on tab hide (some builds) ──
      // Resume immediately so recording continues in the background.
      recorder.onpause = () => {
        console.warn('[MediaRecorder] paused — attempting resume');
        try { if (recorder.state === 'paused') recorder.resume(); } catch (_) {}
      };

      // ── Failure path 3: OS / another app ends the microphone track ─────────
      // On Windows, apps that request exclusive mic access (Teams, Zoom, etc.)
      // can end the track underneath the recorder without any MediaRecorder event.
      stream.getTracks().forEach(track => {
        track.onended = () => {
          if (hasFinalized.current) return;
          console.warn('[MediaRecorder] audio track ended unexpectedly');
          setRecordingError(
            'Microphone disconnected or taken by another application. The audio captured so far has been saved.'
          );
          // Flush the current buffer before finalizing so the last partial
          // timeslice is not lost.
          flushRecorderBuffer();
          // Small delay so ondataavailable fires with the flushed data first.
          setTimeout(finalize, 150);
        };
      });

      // 3-second timeslice: enough chunks to keep the pipeline flowing without
      // flooding the server with a request every second for long meetings.
      recorder.start(3000);
      mediaRecorderRef.current = recorder;
      setRecordingState('recording');
      setRecordingSeconds(0);
      recordingStartTimeRef.current = Date.now();
      acquireWakeLock(); // keep the screen awake for the duration of the meeting

      // Derive elapsed seconds from wall clock so background-tab timer throttling
      // never causes the displayed counter to drift behind the actual audio.
      timerRef.current = setInterval(() => {
        if (recordingStartTimeRef.current) {
          setRecordingSeconds(Math.floor((Date.now() - recordingStartTimeRef.current) / 1000));
        }

        // ── Failure path 4: watchdog ─────────────────────────────────────────
        // Last-resort check: if onerror / track.onended somehow both missed the
        // failure, the interval notices that the recorder went inactive while
        // we still think we are recording, and triggers cleanup.
        if (
          mediaRecorderRef.current?.state === 'inactive' &&
          recordingStartTimeRef.current !== null &&
          !hasFinalized.current
        ) {
          console.warn('[MediaRecorder] watchdog: silent stop detected');
          setRecordingError(
            'Recording stopped unexpectedly. The audio captured so far has been saved.'
          );
          finalize();
        }
      }, 1000);
    } catch (err) {
      if (stream) stream.getTracks().forEach(t => t.stop());
      console.error(err);
      const name = err?.name || '';
      const message = name === 'NotAllowedError'
        ? 'Microphone permission was blocked. Allow microphone access and try again.'
        : name === 'NotFoundError'
          ? 'No microphone was found on this device.'
          : err?.message || 'Microphone permission required.';
      setRecordingError(message);
      alert(message);
    }
  };

  const stopRecording = () => {
    // Force-flush any buffered audio before stop, so the final chunk is captured.
    flushRecorderBuffer();
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    recordingStartTimeRef.current = null;
  };

  const discardRecording = () => {
    // If recording is still running, signal onstop to clean up server-side too.
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      discardedRef.current = true;
      try { mediaRecorderRef.current.stop(); } catch (_) {}
    } else if (recordingDraftIdRef.current || currentDraftId) {
      // Already stopped: explicitly delete the streamed draft on the server.
      const draftId = recordingDraftIdRef.current || currentDraftId;
      axiosInstance.delete(`/api/vision/meeting-drafts/${draftId}`).catch(() => {}).then(loadMyDrafts);
      recordingDraftIdRef.current = null;
      if (currentDraftId === draftId) setCurrentDraftId(null);
    }
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioBlob(null); setAudioUrl(null);
    setRecordingState('idle');
    setRecordingSeconds(0);
    setTranscript('');
    setUploadedFileName('');
    setChunkUploadWarning('');
    setPendingChunkUploads(0);
    pendingChunkUploadsRef.current = 0;
    audioChunksRef.current = [];
  };

  // Reset the form to a fresh state (used after save/finalize, or when starting a new meeting).
  const resetForm = () => {
    setForm({
      mission: '', goal: '',
      meetingDate: new Date().toISOString().split('T')[0], topic: '',
      selectedAgendas: [], participants: [], discussionPoints: [], notes: '', nextMeetingDate: ''
    });
    setCurrentDraftId(null);
    discardRecording();
  };

  const transcribeAndFill = async () => {
    if (!audioBlob) return;
    // Upload still in flight → currentDraftId isn't set yet; proceeding would
    // create a second draft and re-upload the whole file.
    if (audioUploadPct !== null) return;
    if (form.selectedAgendas.length === 0) {
      if (!window.confirm('No agendas selected. AI will infer agendas from the audio. Continue?')) return;
    }
    setRecordingState('transcribing');
    try {
      // Prefer the draft-aware endpoint when we have a draftId (server already has the audio on disk).
      let draftId = currentDraftId;
      if (!draftId) draftId = await createDraftWithAudio(audioBlob);
      const agendasJson = JSON.stringify(form.selectedAgendas.map(a => a.name));
      const { data } = draftId
        ? await axiosInstance.post(
            `/api/vision/meeting-drafts/${draftId}/transcribe`,
            { agendas: agendasJson },
            { timeout: 45 * 60 * 1000 }
          )
        : await (() => {
            const fd = new FormData();
            const ext = (audioBlob.type.includes('webm')) ? 'webm' : (audioBlob.type.includes('mp4') ? 'mp4' : 'wav');
            fd.append('audio', audioBlob, `meeting.${ext}`);
            fd.append('agendas', agendasJson);
            return axiosInstance.post('/api/vision/meeting-minutes/transcribe', fd, {
              headers: { 'Content-Type': 'multipart/form-data' },
              timeout: 45 * 60 * 1000,
            });
          })();
      if (data.success && data.data) {
        const r = data.data;

        // Distribute agendaResults into flat discussionPoints + ensure agenda chips exist
        const newDiscussions = [];
        const inferredAgendas = []; // agendas AI returned that user hadn't selected

        for (const bucket of (r.agendaResults || [])) {
          const bucketName = bucket.agenda || OFF_AGENDA;
          const matched = form.selectedAgendas.find(a => sameAgenda(a.name, bucketName));
          const resolvedName = matched ? matched.name : bucketName;
          const resolvedRef = matched ? matched.agendaId : null;

          // If AI surfaced a non-Off-agenda bucket the user hadn't picked, surface it as a chip too
          if (!matched && !sameAgenda(bucketName, OFF_AGENDA)) {
            if (!inferredAgendas.some(a => sameAgenda(a.name, bucketName))) {
              const existing = agendas.find(a => sameAgenda(a.name, bucketName));
              inferredAgendas.push({ agendaId: existing?._id || null, name: bucketName });
            }
          }

          for (const dp of (bucket.discussionPoints || [])) {
            newDiscussions.push({
              text: dp.point || '',
              agendaName: resolvedName,
              agendaRef: resolvedRef,
              actionItems: (dp.actionItems || []).map(a => ({
                ...blankActionItem(),
                title: a.title || '',
                description: a.description || '',
                deadline: toISODate(a.dueDate),
                priority: a.priority || 'Medium',
                responsible: a.responsible ? [{ externalName: a.responsible }] : [],
                tasks: (a.tasks || []).map(t => ({
                  title: t.title || '',
                  deadline: toISODate(t.deadline),
                  assignee: t.assignee ? [{ externalName: t.assignee }] : []
                }))
              }))
            });
          }
        }

        setForm(f => ({
          ...f,
          topic: r.topic || f.topic,
          selectedAgendas: [...f.selectedAgendas, ...inferredAgendas],
          discussionPoints: newDiscussions.length ? newDiscussions : f.discussionPoints,
          notes: r.notes || f.notes,
          nextMeetingDate: r.nextMeetingDate ? (toISODate(r.nextMeetingDate) || f.nextMeetingDate) : f.nextMeetingDate,
        }));
        setTranscript(r.transcript || '');
        setRecordingState('recorded');
        // Persist the AI-extracted state into the draft on the next tick so we don't lose it.
        setTimeout(() => { persistDraft(); }, 0);
      } else {
        alert('Transcription returned no data.');
        setRecordingState('recorded');
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || err.message || 'Transcription failed');
      setRecordingState('recorded');
    }
  };

  useEffect(() => () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
  }, [audioUrl]);

  // ─── Goals loader (cached per mission) ───────────────────────────────────
  const loadGoals = useCallback(async (missionId) => {
    if (!missionId || goalsByMission[missionId]) return;
    try {
      const { data } = await axiosInstance.get(`/api/vision/goals?missionId=${missionId}`);
      setGoalsByMission(prev => ({ ...prev, [missionId]: data.data || [] }));
    } catch (err) { console.error(err); }
  }, [goalsByMission]);

  useEffect(() => { if (form.mission) loadGoals(form.mission); }, [form.mission, loadGoals]);
  useEffect(() => { if (filterMission) loadGoals(filterMission); }, [filterMission, loadGoals]);

  // ─── Minutes loader ──────────────────────────────────────────────────────
  const loadMinutes = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterGoal) params.append('goalId', filterGoal);
      else if (filterMission) params.append('missionId', filterMission);
      params.append('state', lockState);
      const qs = params.toString();
      const { data } = await axiosInstance.get(`/api/vision/meeting-minutes${qs ? '?' + qs : ''}`);
      setMinutes(data.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterMission, filterGoal, lockState]);

  useEffect(() => { loadMinutes(); }, [loadMinutes]);

  // ─── Agendas available for the selected goal ─────────────────────────────
  useEffect(() => {
    if (!form.goal) { setAgendas([]); return; }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axiosInstance.get(`/api/vision/agendas?goalId=${form.goal}`);
        if (!cancelled) setAgendas(data.data || []);
      } catch (err) { console.error(err); }
    })();
    return () => { cancelled = true; };
  }, [form.goal]);

  // When goal changes, clear selected agendas + discussion points (they were keyed to the old goal)
  useEffect(() => {
    setForm(f => ({ ...f, selectedAgendas: [], discussionPoints: [] }));
  }, [form.goal]);

  const toggleAgenda = (agenda) => {
    setForm(f => {
      const isOn = f.selectedAgendas.some(a => a.agendaId === agenda._id);
      let next;
      if (isOn) {
        next = f.selectedAgendas.filter(a => a.agendaId !== agenda._id);
      } else {
        next = [...f.selectedAgendas, { agendaId: agenda._id, name: agenda.name }];
      }
      return { ...f, selectedAgendas: next };
    });
  };

  const handleAddAgenda = async () => {
    const name = newAgendaName.trim();
    if (!name || !form.goal) return;
    try {
      const { data } = await axiosInstance.post('/api/vision/agendas', { name, goal: form.goal });
      if (data.success) {
        const agenda = data.data;
        setAgendas(prev => prev.some(a => a._id === agenda._id) ? prev : [...prev, agenda]);
        setForm(f => ({
          ...f,
          selectedAgendas: f.selectedAgendas.some(a => a.agendaId === agenda._id)
            ? f.selectedAgendas
            : [...f.selectedAgendas, { agendaId: agenda._id, name: agenda.name }]
        }));
        setNewAgendaName('');
      }
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to add agenda');
    }
  };

  // ─── Discussion / Action item / Task helpers (operate on flat array) ─────
  const updateDiscussion = (di, patch) => setForm(f => ({
    ...f,
    discussionPoints: f.discussionPoints.map((d, idx) => idx === di ? { ...d, ...patch } : d)
  }));
  const addDiscussionUnder = (agendaName, agendaRef) => setForm(f => ({
    ...f,
    discussionPoints: [...f.discussionPoints, blankDiscussion(agendaName, agendaRef)]
  }));
  const removeDiscussion = (di) => setForm(f => ({
    ...f,
    discussionPoints: f.discussionPoints.filter((_, i) => i !== di)
  }));

  const addActionItem = (di) => updateDiscussion(di, {
    actionItems: [...form.discussionPoints[di].actionItems, blankActionItem()]
  });
  const updateActionItem = (di, ai, patch) => setForm(f => {
    const dps = f.discussionPoints.map((d, i) => {
      if (i !== di) return d;
      return { ...d, actionItems: d.actionItems.map((a, j) => j === ai ? { ...a, ...patch } : a) };
    });
    return { ...f, discussionPoints: dps };
  });
  const removeActionItem = (di, ai) => setForm(f => {
    const dps = f.discussionPoints.map((d, i) => i !== di ? d : ({
      ...d, actionItems: d.actionItems.filter((_, j) => j !== ai)
    }));
    return { ...f, discussionPoints: dps };
  });

  const addTask = (di, ai) => updateActionItem(di, ai, {
    tasks: [...form.discussionPoints[di].actionItems[ai].tasks, blankTask()]
  });
  const updateTask = (di, ai, ti, patch) => setForm(f => {
    const dps = f.discussionPoints.map((d, i) => {
      if (i !== di) return d;
      return {
        ...d,
        actionItems: d.actionItems.map((a, j) => {
          if (j !== ai) return a;
          return { ...a, tasks: a.tasks.map((t, k) => k === ti ? { ...t, ...patch } : t) };
        })
      };
    });
    return { ...f, discussionPoints: dps };
  });
  const removeTask = (di, ai, ti) => setForm(f => {
    const dps = f.discussionPoints.map((d, i) => {
      if (i !== di) return d;
      return {
        ...d,
        actionItems: d.actionItems.map((a, j) => {
          if (j !== ai) return a;
          return { ...a, tasks: a.tasks.filter((_, k) => k !== ti) };
        })
      };
    });
    return { ...f, discussionPoints: dps };
  });

  // Buckets to render: each selected agenda + Off-agenda (only if it has points)
  const formBuckets = useMemo(() => {
    const buckets = form.selectedAgendas.map(a => ({
      name: a.name,
      ref: a.agendaId,
      indexes: []
    }));
    const offAgendaIndexes = [];
    form.discussionPoints.forEach((dp, idx) => {
      const found = buckets.find(b => sameAgenda(b.name, dp.agendaName));
      if (found) found.indexes.push(idx);
      else offAgendaIndexes.push(idx);
    });
    if (offAgendaIndexes.length) {
      buckets.push({ name: OFF_AGENDA, ref: null, indexes: offAgendaIndexes });
    }
    return buckets;
  }, [form.selectedAgendas, form.discussionPoints]);

  // ─── Submit ──────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!form.goal || !form.meetingDate) { alert('Goal and meeting date are required.'); return; }
    setSaving(true);
    try {
      const payload = {
        goal: form.goal,
        meetingDate: form.meetingDate,
        topic: form.topic,
        agendas: form.selectedAgendas.map(a => ({ agendaId: a.agendaId, name: a.name })),
        participants: form.participants,
        notes: form.notes,
        nextMeetingDate: toISODate(form.nextMeetingDate) || undefined,
        discussionPoints: form.discussionPoints
          .filter(d => d.text.trim() || d.actionItems.length > 0)
          .map(d => ({
            text: d.text,
            agendaName: d.agendaName || OFF_AGENDA,
            agendaRef: d.agendaRef || undefined,
            actionItems: d.actionItems
              .filter(a => a.title.trim())
              .map(a => ({
                title: a.title.trim(),
                description: a.description,
                deadline: toISODate(a.deadline) || undefined,
                priority: a.priority,
                responsible: a.responsible,
                tasks: a.tasks
                  .filter(t => t.title.trim())
                  .map(t => ({
                    title: t.title.trim(),
                    deadline: toISODate(t.deadline) || undefined,
                    assignee: t.assignee
                  }))
              }))
          }))
      };
      const endpoint = currentDraftId
        ? `/api/vision/meeting-drafts/${currentDraftId}/finalize`
        : '/api/vision/meeting-minutes';
      const { data } = await axiosInstance.post(endpoint, payload);
      setMinutes(prev => [data.data, ...prev]);
      resetForm();
      setShowForm(false);
      loadMyDrafts();
      onMissionUpdate?.();
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to save meeting minutes.');
    } finally { setSaving(false); }
  };

  // "Save Draft" button — explicit checkpoint. Only allowed while a draft exists
  // (i.e. user already recorded/uploaded audio). For a meeting with no audio,
  // there's no draft yet — they'd just click Save Meeting instead.
  const handleSaveDraft = async () => {
    if (!currentDraftId) {
      alert('Record or upload audio first — drafts are tied to a recording.');
      return;
    }
    const saved = await persistDraft();
    if (saved) alert('Draft saved. You can resume it anytime from the Drafts panel.');
  };

  // Resume an existing draft: load its form state into the editor and fetch its
  // audio file for playback.
  const resumeDraft = async (draftId) => {
    setResumingDraftId(draftId);
    try {
      const { data } = await axiosInstance.get(`/api/vision/meeting-drafts/${draftId}`);
      if (!data.success) return;
      const d = data.data;
      setForm({
        mission: d.mission || '',
        goal: d.goal || '',
        meetingDate: d.meetingDate || new Date().toISOString().split('T')[0],
        topic: d.topic || '',
        selectedAgendas: d.agendas || [],
        participants: d.participants || [],
        discussionPoints: d.discussionPoints || [],
        notes: d.notes || '',
        nextMeetingDate: d.nextMeetingDate || ''
      });
      setCurrentDraftId(d._id);
      recordingDraftIdRef.current = d._id;
      setTranscript(d.transcript || '');
      setShowForm(true);

      // Pull audio if present so the user can play it back.
      if (d.audioFileName) {
        try {
          const resp = await axiosInstance.get(`/api/vision/meeting-drafts/${d._id}/audio`, { responseType: 'blob' });
          const blob = resp.data;
          if (audioUrl) URL.revokeObjectURL(audioUrl);
          setAudioBlob(blob);
          const url = URL.createObjectURL(blob);
          setAudioUrl(url);
          setUploadedFileName(d.audioFileName);
          setRecordingState('recorded');

          // Get actual duration (handles webm Infinity case via seek-trick).
          getRealAudioDuration(url).then((dur) => {
            if (dur > 0) setRecordingSeconds(Math.floor(dur));
          });
        } catch (err) { console.error('audio fetch failed', err); }
      }
    } catch (err) {
      console.error('resumeDraft:', err);
      alert('Failed to load draft.');
    } finally {
      setResumingDraftId(null);
    }
  };

  const deleteDraft = async (draftId) => {
    if (!window.confirm('Delete this draft? The recorded audio will also be removed.')) return;
    try {
      await axiosInstance.delete(`/api/vision/meeting-drafts/${draftId}`);
      if (currentDraftId === draftId) resetForm();
      loadMyDrafts();
    } catch (err) {
      console.error('deleteDraft:', err);
      alert(err.response?.data?.message || 'Failed to delete draft.');
    }
  };

  const closeForm = () => { resetForm(); setShowForm(false); };

  const handleDelete = async (id) => {
    const m = minutes.find(x => x._id === id);
    const aiCount = (m?.discussionPoints || []).reduce((s, dp) => s + (dp.actionItems?.length || 0), 0);
    const msg = aiCount > 0
      ? `This will also DELETE ${aiCount} action item${aiCount === 1 ? '' : 's'} and all tasks under them. Goal & mission progress will be recalculated.\n\nContinue?`
      : 'Delete this meeting record?';
    if (!window.confirm(msg)) return;
    try {
      const { data } = await axiosInstance.delete(`/api/vision/meeting-minutes/${id}`);
      setMinutes(prev => prev.filter(x => x._id !== id));
      if (data?.cascaded?.actionItems > 0) onMissionUpdate?.();
    } catch (err) { console.error(err); }
  };

  // A locked mission is a closed phase — POST /meeting-minutes is lock-guarded and
  // answers 423, so it must never be offered as a target in the create form. The
  // filter bar below still lists every mission the board loaded.
  const openMissions = missions.filter(m => !m.isLocked);
  const formMissionGoals = form.mission ? (goalsByMission[form.mission] || []) : [];
  const filterMissionGoals = filterMission ? (goalsByMission[filterMission] || []) : [];
  const recordingSaveStatus = !isOnline
    ? 'Offline: recording is held in this window until the connection returns.'
    : pendingChunkUploads > 0
      ? `Saving audio chunk${pendingChunkUploads === 1 ? '' : 's'} to draft...`
      : currentDraftId
        ? `Server draft active (${isInstalledApp ? 'installed app' : 'browser'})`
        : 'Server draft will start before recording.';

  // ─── Group existing minute's discussion points by agenda for display ─────
  const groupMinutePoints = (minute) => {
    const order = (minute.agendas || []).map(a => a.name);
    const buckets = {};
    (minute.discussionPoints || []).forEach(dp => {
      const key = dp.agendaName || OFF_AGENDA;
      if (!buckets[key]) buckets[key] = [];
      buckets[key].push(dp);
    });
    const ordered = [];
    order.forEach(name => { if (buckets[name]) { ordered.push({ name, points: buckets[name] }); delete buckets[name]; } });
    Object.entries(buckets).forEach(([name, points]) => ordered.push({ name, points }));
    return ordered;
  };

  return (
    <div>
      {/* Full-screen blocker while a draft (+ its audio) is being downloaded. */}
      {resumingDraftId && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: 16,
          cursor: 'wait',
        }}>
          <div style={{
            width: 48, height: 48, borderRadius: '50%',
            border: '4px solid rgba(255,255,255,0.25)',
            borderTopColor: '#fff',
            animation: 'mm-spin 0.8s linear infinite',
          }} />
          <div style={{ color: '#fff', fontSize: 15, fontWeight: 500, letterSpacing: 0.2 }}>
            Loading draft…
          </div>
          <style>{`@keyframes mm-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      )}

      <div className="v26-section-header" data-tour="meetings-header">
        <span className="v26-section-title">Meeting Minutes</span>
        <button className="v26-btn v26-btn--primary" onClick={() => setShowForm(s => !s)}>
          <HiOutlinePlus size={16} /> Add Minutes
        </button>
      </div>

      {/* My Drafts — recordings/meetings in progress, scoped to the current user. */}
      {myDrafts.length > 0 && (
        <div className="v26-panel" style={{ marginBottom: 12, background: '#fffaf0', borderLeft: '3px solid #f59e0b' }}>
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#92400e' }}>
            My Drafts ({myDrafts.length}) — these are saved on the server. Resume anytime; deleted after 30 days.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {myDrafts.map(d => (
              <div key={d._id} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px',
                background: '#fff', border: '1px solid #fde68a', borderRadius: 6
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#1f2937' }}>
                    {d.topic || '(untitled meeting)'}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7280' }}>
                    {d.audioFileName ? '🎙 audio attached · ' : ''}
                    stage: {d.stage} · updated {new Date(d.updatedAt).toLocaleString('en-GB')}
                  </div>
                </div>
                <button
                  type="button"
                  className="v26-btn v26-btn--sm"
                  onClick={() => resumeDraft(d._id)}
                  disabled={!!resumingDraftId}
                >
                  Resume
                </button>
                <button type="button" className="v26-btn v26-btn--danger v26-btn--sm" onClick={() => deleteDraft(d._id)}>
                  <HiOutlineTrash size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <div className="v26-panel">
          <div className="v26-form">
            <div className="v26-form-row">
              <div className="v26-form-group" style={{ flex: 1 }}>
                <label>Mission *</label>
                <select value={form.mission}
                  onChange={e => setForm(f => ({ ...f, mission: e.target.value, goal: '' }))}>
                  <option value="">{openMissions.length ? 'Select mission…' : 'No unlocked missions — unlock one first'}</option>
                  {openMissions.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
                </select>
              </div>
              <div className="v26-form-group" style={{ flex: 1 }}>
                <label>Goal *</label>
                <select value={form.goal}
                  onChange={e => setForm(f => ({ ...f, goal: e.target.value }))}
                  disabled={!form.mission}>
                  <option value="">{form.mission ? (formMissionGoals.length ? 'Select goal…' : 'No goals — add one in the Roadmap first') : 'Pick mission first'}</option>
                  {formMissionGoals.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
                </select>
              </div>
              <div className="v26-form-group">
                <label>Meeting Date *</label>
                <input type="date" value={form.meetingDate} onChange={e => setForm(f => ({ ...f, meetingDate: e.target.value }))} />
              </div>
              <div className="v26-form-group">
                <label>Next Meeting</label>
                <input type="date" value={form.nextMeetingDate} onChange={e => setForm(f => ({ ...f, nextMeetingDate: e.target.value }))} />
              </div>
            </div>

            <div className="v26-form-group">
              <label>Meeting Title (optional)</label>
              <input
                type="text"
                value={form.topic}
                onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
                placeholder="e.g. Weekly leadership sync (auto-filled by AI if blank)"
              />
            </div>

            <div className="v26-form-group">
              <label>Participants <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional, can include external)</span></label>
              <AssigneePicker
                value={form.participants}
                onChange={(arr) => setForm(f => ({ ...f, participants: arr }))}
                systemUsers={systemUsers}
                placeholder="Add a participant…"
              />
            </div>

            {/* Multi-agenda picker */}
            <div className="v26-form-group">
              <label>Agendas {form.selectedAgendas.length > 0 && <span style={{ color: '#64748b', fontWeight: 400 }}>({form.selectedAgendas.length} selected)</span>}</label>
              {!form.goal ? (
                <div style={{ fontSize: 12, color: '#94a3b8', padding: 8, background: '#f8fafc', borderRadius: 6 }}>Pick a goal first</div>
              ) : (
                <div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                    {agendas.length === 0 && (
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>No agendas yet — add one below.</span>
                    )}
                    {agendas.map(a => {
                      const selected = form.selectedAgendas.some(s => s.agendaId === a._id);
                      return (
                        <button
                          key={a._id}
                          type="button"
                          onClick={() => toggleAgenda(a)}
                          style={{
                            padding: '4px 10px',
                            fontSize: 12,
                            borderRadius: 999,
                            border: '1px solid ' + (selected ? '#7c3aed' : '#cbd5e1'),
                            background: selected ? '#ede9fe' : '#fff',
                            color: selected ? '#5b21b6' : '#475569',
                            fontWeight: selected ? 600 : 400,
                            cursor: 'pointer'
                          }}
                        >
                          {selected ? '✓ ' : ''}{a.name}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      style={{ flex: 1, fontSize: 12, padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 6 }}
                      value={newAgendaName}
                      onChange={e => setNewAgendaName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddAgenda(); } }}
                      placeholder="Add a new agenda for this goal…"
                    />
                    <button type="button" className="v26-btn v26-btn--secondary v26-btn--sm"
                      onClick={handleAddAgenda} disabled={!newAgendaName.trim()}>
                      <HiOutlinePlus size={12} /> Add
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* AI Auto-Fill */}
            <div style={{
              border: '1px dashed #c084fc', borderRadius: 10, padding: 12,
              background: 'linear-gradient(135deg, #faf5ff 0%, #fdf2f8 100%)', marginBottom: 4,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <HiOutlineSparkles size={16} style={{ color: '#9333ea' }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: '#6b21a8', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                  AI Auto-Fill (BETA)
                </span>
                <span style={{ fontSize: 11, color: '#7c3aed', fontStyle: 'italic' }}>
                  Record meeting → AI groups discussion under your agendas
                </span>
              </div>
              {form.selectedAgendas.length > 0 && (
                <div style={{ fontSize: 11, color: '#6b21a8', marginBottom: 8 }}>
                  AI will bucket the meeting under: <strong>{form.selectedAgendas.map(a => a.name).join(' · ')}</strong>
                </div>
              )}
              {/* Interruption banner — shown whenever recording stopped for a reason
                  other than the user pressing Stop (mic disconnect, OS exclusive
                  access, browser error, etc.).  Dismissed with × or auto-clears
                  when a new recording starts. */}
              {recordingError && (
                <div style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6,
                  padding: '8px 10px', marginBottom: 8,
                  fontSize: 12, color: '#991b1b', lineHeight: 1.5,
                }}>
                  <span style={{ flexShrink: 0, fontSize: 14 }}>⚠️</span>
                  <span style={{ flex: 1 }}>{recordingError}</span>
                  <button
                    type="button"
                    onClick={() => setRecordingError(null)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: '#991b1b', fontSize: 16, lineHeight: 1,
                      padding: 0, flexShrink: 0,
                    }}
                    title="Dismiss"
                  >×</button>
                </div>
              )}

              {chunkUploadWarning && recordingState !== 'recording' && (
                <div style={{
                  background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6,
                  padding: '8px 10px', marginBottom: 8,
                  fontSize: 12, color: '#92400e', lineHeight: 1.5,
                }}>
                  {chunkUploadWarning}
                </div>
              )}

              {recordingState === 'idle' && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button
                    type="button"
                    className="v26-btn v26-btn--primary v26-btn--sm"
                    onClick={startRecording}
                    disabled={!recordingSupport.supported}
                    title={recordingSupport.supported ? 'Start recording' : recordingSupport.reason}
                    style={{ background: '#9333ea', borderColor: '#9333ea' }}
                  >
                    <HiOutlineMicrophone size={14} /> Start Recording
                  </button>
                  <span style={{ fontSize: 12, color: '#7c3aed' }}>or</span>
                  <button type="button" className="v26-btn v26-btn--secondary v26-btn--sm"
                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                    style={{ borderColor: '#c084fc', color: '#6b21a8' }}>
                    📁 Upload Audio File
                  </button>
                  <input ref={fileInputRef} type="file"
                    accept="audio/*,.mp3,.wav,.m4a,.ogg,.webm,.aac,.flac,.opus"
                    onChange={handleFileUpload} style={{ display: 'none' }} />
                  {!recordingSupport.supported && (
                    <span style={{ flexBasis: '100%', fontSize: 11, color: '#991b1b' }}>
                      {recordingSupport.reason}
                    </span>
                  )}
                </div>
              )}
              {recordingState === 'recording' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#ef4444', animation: 'v26-pulse 1.2s infinite' }} />
                    <span style={{ fontSize: 13, color: '#991b1b', fontWeight: 600 }}>
                      Recording... {formatDuration(recordingSeconds)}
                    </span>
                    <button type="button" className="v26-btn v26-btn--danger v26-btn--sm" onClick={stopRecording}>
                      <HiOutlineStop size={14} /> Stop
                    </button>
                  </div>
                  <div style={{ fontSize: 11, color: isOnline ? '#6b21a8' : '#92400e', lineHeight: 1.45 }}>
                    {recordingSaveStatus}
                  </div>
                  {chunkUploadWarning && (
                    <div style={{
                      fontSize: 11, color: '#92400e', background: '#fffbeb',
                      border: '1px solid #fcd34d', borderRadius: 6, padding: '6px 8px',
                    }}>
                      {chunkUploadWarning}
                    </div>
                  )}
                </div>
              )}
              {recordingState === 'recorded' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {uploadedFileName && <div style={{ fontSize: 12, color: '#6b21a8', fontWeight: 600 }}>📎 {uploadedFileName}</div>}
                  {pendingChunkUploads > 0 && (
                    <div style={{ fontSize: 11, color: '#6b21a8' }}>{recordingSaveStatus}</div>
                  )}
                  {audioUrl && (
                    <audio
                      key={audioUrl}
                      src={audioUrl}
                      controls
                      preload="metadata"
                      style={{ width: '100%', maxWidth: 420, height: 36 }}
                      onError={(e) => console.error('Audio playback error:', e)}
                      onLoadedMetadata={(e) => {
                        const el = e.target;
                        // WebM from MediaRecorder reports Infinity until the browser scans the file —
                        // a seek-to-end forces the scan, then we snap back to 0.
                        fixAudioElementDuration(el);
                        if (Number.isFinite(el.duration) && el.duration > 0) {
                          const actualDuration = Math.floor(el.duration);
                          if (Math.abs(actualDuration - recordingSeconds) > 2) {
                            setRecordingSeconds(actualDuration);
                          }
                        }
                      }}
                      onDurationChange={(e) => {
                        const el = e.target;
                        if (Number.isFinite(el.duration) && el.duration > 0) {
                          const actualDuration = Math.floor(el.duration);
                          if (Math.abs(actualDuration - recordingSeconds) > 2) {
                            setRecordingSeconds(actualDuration);
                          }
                        }
                      }}
                    />
                  )}
                  {audioUploadPct !== null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: 420 }}>
                      <div style={{ flex: 1, height: 6, background: '#ede9fe', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${audioUploadPct}%`, height: '100%', background: '#9333ea', borderRadius: 3, transition: 'width 0.2s ease' }} />
                      </div>
                      <span style={{ fontSize: 11, color: '#6b21a8', fontWeight: 600, whiteSpace: 'nowrap' }}>
                        {audioUploadPct < 100 ? `Uploading audio... ${audioUploadPct}%` : 'Saving on server...'}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <button type="button" className="v26-btn v26-btn--primary v26-btn--sm" onClick={transcribeAndFill} disabled={audioUploadPct !== null} style={{ background: '#9333ea', borderColor: '#9333ea', opacity: audioUploadPct !== null ? 0.5 : 1 }}>
                      <HiOutlineSparkles size={14} /> Transcribe & Auto-Fill
                    </button>
                    <button type="button" className="v26-btn v26-btn--secondary v26-btn--sm" onClick={discardRecording} disabled={audioUploadPct !== null} style={{ opacity: audioUploadPct !== null ? 0.5 : 1 }}>Discard</button>
                    <button type="button" className="v26-btn v26-btn--secondary v26-btn--sm" onClick={startRecording} disabled={audioUploadPct !== null} style={{ opacity: audioUploadPct !== null ? 0.5 : 1 }}>
                      <HiOutlineMicrophone size={14} /> Re-record
                    </button>
                  </div>
                  {transcript && (
                    <details style={{ marginTop: 4 }}>
                      <summary style={{ fontSize: 12, color: '#6b21a8', cursor: 'pointer', fontWeight: 600 }}>View transcript</summary>
                      <div style={{ marginTop: 6, padding: 8, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, color: '#374151', whiteSpace: 'pre-wrap', maxHeight: 180, overflowY: 'auto' }}>
                        {transcript}
                      </div>
                    </details>
                  )}
                </div>
              )}
              {recordingState === 'transcribing' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#6b21a8', fontSize: 13, fontWeight: 600 }}>
                  <HiOutlineSparkles size={16} className="v26-spin" />
                  Transcribing & grouping by agenda...
                </div>
              )}
            </div>

            {/* Discussion Points grouped by agenda */}
            <div style={{ marginTop: 12 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Discussion Points
              </label>
              {formBuckets.length === 0 && (
                <div style={{ fontSize: 12, color: '#94a3b8', padding: 10, background: '#f8fafc', borderRadius: 6, marginTop: 8 }}>
                  Select at least one agenda above, then add discussion points under it.
                </div>
              )}
              {formBuckets.map(bucket => (
                <div key={bucket.name} style={{
                  marginTop: 12,
                  background: bucket.name === OFF_AGENDA ? '#fef3c7' : '#f1f5f9',
                  border: '1px solid ' + (bucket.name === OFF_AGENDA ? '#fcd34d' : '#cbd5e1'),
                  borderRadius: 8, padding: 10
                }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: bucket.name === OFF_AGENDA ? '#92400e' : '#334155', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                    {bucket.name === OFF_AGENDA ? '⚠ Off-agenda' : '📌 ' + bucket.name}
                    <span style={{ fontWeight: 400, marginLeft: 6, color: '#64748b' }}>· {bucket.indexes.length} point{bucket.indexes.length === 1 ? '' : 's'}</span>
                  </div>

                  {bucket.indexes.map(di => {
                    const dp = form.discussionPoints[di];
                    return (
                      <div key={di} style={{
                        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
                        padding: 10, marginTop: 6
                      }}>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: '#64748b', minWidth: 22 }}>#{di + 1}</span>
                          <input
                            style={{ flex: 1, border: '1px solid #d1d5db', borderRadius: 6, padding: '6px 10px', fontSize: 13 }}
                            value={dp.text}
                            onChange={e => updateDiscussion(di, { text: e.target.value })}
                            placeholder="What was discussed..." />
                          <button className="v26-btn v26-btn--danger v26-btn--sm" onClick={() => removeDiscussion(di)} title="Remove discussion">
                            <HiOutlineTrash size={11} />
                          </button>
                        </div>

                        <div style={{ marginTop: 8, paddingLeft: 28 }}>
                          {dp.actionItems.map((ai, aiIdx) => (
                            <div key={aiIdx} style={{
                              background: '#fafafa', border: '1px solid #e2e8f0', borderRadius: 6,
                              padding: 8, marginBottom: 6
                            }}>
                              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                                <input
                                  style={{ flex: 2, minWidth: 140, border: '1px solid #e2e8f0', borderRadius: 4, padding: '4px 8px', fontSize: 12 }}
                                  value={ai.title}
                                  onChange={e => updateActionItem(di, aiIdx, { title: e.target.value })}
                                  placeholder="Action item title" />
                                <input type="date"
                                  style={{ fontSize: 12, padding: '4px 8px', border: '1px solid #e2e8f0', borderRadius: 4 }}
                                  value={ai.deadline}
                                  onChange={e => updateActionItem(di, aiIdx, { deadline: e.target.value })} />
                                <select
                                  style={{ fontSize: 12, padding: '4px 6px', border: '1px solid #e2e8f0', borderRadius: 4 }}
                                  value={ai.priority}
                                  onChange={e => updateActionItem(di, aiIdx, { priority: e.target.value })}>
                                  {['Low','Medium','High','Urgent'].map(p => <option key={p}>{p}</option>)}
                                </select>
                                <button className="v26-btn v26-btn--danger v26-btn--sm" onClick={() => removeActionItem(di, aiIdx)}>
                                  <HiOutlineTrash size={10} />
                                </button>
                              </div>
                              {ai.description && (
                                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4, fontStyle: 'italic' }}>
                                  {ai.description}
                                </div>
                              )}
                              <div style={{ marginTop: 4, paddingLeft: 4 }}>
                                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600, marginRight: 6 }}>Responsible:</span>
                                <AssigneePicker
                                  value={ai.responsible}
                                  onChange={(arr) => updateActionItem(di, aiIdx, { responsible: arr })}
                                  systemUsers={systemUsers}
                                  compact
                                  placeholder="Add person…"
                                />
                              </div>

                              <div style={{ paddingLeft: 16, marginTop: 6 }}>
                                {ai.tasks.map((t, ti) => (
                                  <div key={ti} style={{ display: 'flex', gap: 4, marginBottom: 4, flexWrap: 'wrap', alignItems: 'center' }}>
                                    <span style={{ fontSize: 11, color: '#94a3b8', alignSelf: 'center', minWidth: 18 }}>↳</span>
                                    <input
                                      style={{ flex: 2, minWidth: 120, fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 3 }}
                                      value={t.title}
                                      onChange={e => updateTask(di, aiIdx, ti, { title: e.target.value })}
                                      placeholder="Task title" />
                                    <input type="date"
                                      style={{ fontSize: 11, padding: '3px 6px', border: '1px solid #e2e8f0', borderRadius: 3 }}
                                      value={t.deadline}
                                      onChange={e => updateTask(di, aiIdx, ti, { deadline: e.target.value })} />
                                    <div style={{ flex: 2, minWidth: 160 }}>
                                      <AssigneePicker
                                        value={t.assignee}
                                        onChange={(arr) => updateTask(di, aiIdx, ti, { assignee: arr })}
                                        systemUsers={systemUsers}
                                        compact
                                        placeholder="Assignees…"
                                      />
                                    </div>
                                    <button onClick={() => removeTask(di, aiIdx, ti)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 2 }}>
                                      <HiOutlineTrash size={10} />
                                    </button>
                                  </div>
                                ))}
                                <button className="v26-btn v26-btn--secondary v26-btn--sm" style={{ fontSize: 10, marginTop: 2 }} onClick={() => addTask(di, aiIdx)}>
                                  + Task
                                </button>
                              </div>
                            </div>
                          ))}
                          <button className="v26-btn v26-btn--secondary v26-btn--sm" style={{ fontSize: 11 }} onClick={() => addActionItem(di)}>
                            <HiOutlinePlus size={11} /> Add Action Item
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {bucket.name !== OFF_AGENDA && (
                    <button
                      className="v26-btn v26-btn--secondary v26-btn--sm"
                      style={{ marginTop: 8, fontSize: 11 }}
                      onClick={() => addDiscussionUnder(bucket.name, bucket.ref)}
                    >
                      + Add point under "{bucket.name}"
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="v26-form-group" style={{ marginTop: 12 }}>
              <label>Additional Notes</label>
              <textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Any other notes..." />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
              {currentDraftId && (
                <span style={{ fontSize: 11, color: '#92400e', marginRight: 'auto' }}>
                  📝 Draft active — your work is saved on the server
                </span>
              )}
              <button className="v26-btn v26-btn--secondary" onClick={closeForm}>Cancel</button>
              {currentDraftId && (
                <button
                  type="button"
                  className="v26-btn v26-btn--secondary"
                  onClick={handleSaveDraft}
                  disabled={savingDraft}
                  title="Update the saved draft with your current edits"
                >
                  {savingDraft ? 'Saving draft...' : 'Save Draft'}
                </button>
              )}
              <button className="v26-btn v26-btn--primary" onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving...' : 'Save Minutes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="v26-filters">
        <select value={filterMission}
          onChange={e => { setFilterMission(e.target.value); setFilterGoal(''); }}>
          <option value="">All Missions</option>
          {missions.map(m => <option key={m._id} value={m._id}>{m.name}</option>)}
        </select>
        <select value={filterGoal}
          onChange={e => setFilterGoal(e.target.value)}
          disabled={!filterMission}>
          <option value="">All Goals</option>
          {filterMissionGoals.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
        </select>
        {(filterMission || filterGoal) && (
          <button className="v26-btn v26-btn--secondary v26-btn--sm" onClick={() => { setFilterMission(''); setFilterGoal(''); }}>Clear</button>
        )}
      </div>

      {loading ? <div className="v26-empty">Loading meeting minutes...</div>
        : minutes.length === 0 ? <div className="v26-empty">No meeting minutes found.</div>
        : (
        <div>
          {minutes.map(m => {
            const grouped = groupMinutePoints(m);
            return (
              <div key={m._id} className="v26-meeting-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <div className="v26-meeting-date">
                      {new Date(m.meetingDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </div>
                    <div className="v26-meeting-title">{m.topic || 'Meeting Record'}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 4, fontSize: 11, color: '#475569', alignItems: 'center' }}>
                      <span>
                        <span style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: 4 }}>Mission</span>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>{m.mission?.name || '—'}</span>
                      </span>
                      <span style={{ color: '#cbd5e1' }}>›</span>
                      <span>
                        <span style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: 4 }}>Goal</span>
                        <span style={{ fontWeight: 600, color: '#1e293b' }}>{m.goal?.name || '—'}</span>
                      </span>
                    </div>
                    {(m.agendas || []).length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 9, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginRight: 2 }}>
                          Agenda{m.agendas.length > 1 ? 's' : ''}
                        </span>
                        {m.agendas.map((a, i) => (
                          <span key={i} style={{
                            fontSize: 10, padding: '2px 8px', borderRadius: 999,
                            background: '#ede9fe', color: '#5b21b6', fontWeight: 600
                          }}>{a.name}</span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <button className="v26-btn v26-btn--secondary v26-btn--sm"
                      onClick={() => setExpandedId(expandedId === m._id ? null : m._id)}>
                      {expandedId === m._id ? <HiOutlineChevronUp size={14} /> : <HiOutlineChevronDown size={14} />}
                    </button>
                    {canEdit && (
                      <button className="v26-btn v26-btn--secondary v26-btn--sm" title="Edit meeting"
                        onClick={() => setEditingMinute(m)}>
                        <HiOutlinePencil size={13} />
                      </button>
                    )}
                    {canEdit && (
                      <button className="v26-btn v26-btn--danger v26-btn--sm" onClick={() => handleDelete(m._id)}>
                        <HiOutlineTrash size={13} />
                      </button>
                    )}
                  </div>
                </div>

                {expandedId === m._id && (
                  <div style={{ marginTop: 12 }}>
                    {(m.participants || []).length > 0 && (
                      <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                          Participants ({m.participants.length})
                        </span>
                        {m.participants.map((p, idx) => {
                          const isExt = !p.userId;
                          const label = p.userId
                            ? (typeof p.userId === 'object'
                                ? (p.userId.fullName || p.userId.username)
                                : (systemUsers.find(u => u._id === p.userId)?.fullName || systemUsers.find(u => u._id === p.userId)?.username || 'User'))
                            : (p.externalName || '?');
                          return (
                            <span key={idx} style={{
                              display: 'inline-block',
                              background: isExt ? '#fef3c7' : '#e0e7ff',
                              color: isExt ? '#92400e' : '#3730a3',
                              padding: '2px 8px', borderRadius: 99, fontSize: 11, fontWeight: 500
                            }}>{label}</span>
                          );
                        })}
                      </div>
                    )}
                    {grouped.map((bucket, bi) => (
                      <div key={bi} style={{ marginBottom: 14 }}>
                        <div style={{
                          fontSize: 11, fontWeight: 700,
                          color: bucket.name === OFF_AGENDA ? '#92400e' : '#5b21b6',
                          textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6
                        }}>
                          {bucket.name === OFF_AGENDA ? '⚠ Off-agenda' : '📌 ' + bucket.name}
                        </div>
                        {bucket.points.map((dp, i) => (
                          <div key={i} style={{ marginBottom: 10, paddingLeft: 6 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', marginBottom: 4 }}>
                              {i + 1}. {dp.text || '(no text)'}
                            </div>
                            {dp.actionItems?.length > 0 && (
                              <div style={{ paddingLeft: 14, borderLeft: '2px solid #e2e8f0' }}>
                                {dp.actionItems.map(ai => (
                                  <div key={ai._id} style={{ marginBottom: 6, fontSize: 12 }}>
                                    <div style={{ fontWeight: 600, color: '#0f172a' }}>
                                      ▸ {ai.title}
                                      <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 6, fontSize: 11 }}>
                                        ({ai.completionPercentage}% · {ai.status})
                                      </span>
                                    </div>
                                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                      {ai.deadline && <span>📅 {new Date(ai.deadline).toLocaleDateString('en-GB')}</span>}
                                      {(ai.responsible || []).length > 0 && (
                                        <span>👤 {(ai.responsible || []).map(r => r.userId?.fullName || r.externalName || '?').join(', ')}</span>
                                      )}
                                      {ai.priority && <span>⚡ {ai.priority}</span>}
                                      <span>⚖ {ai.weight}%</span>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                    {m.notes && <div style={{ marginTop: 10, fontSize: 13, color: '#64748b', fontStyle: 'italic' }}>{m.notes}</div>}
                    {m.nextMeetingDate && (
                      <div style={{ marginTop: 8, fontSize: 12, color: '#711b45', fontWeight: 600 }}>
                        Next Meeting: {new Date(m.nextMeetingDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </div>
                    )}
                    <AttachmentList
                      entityType="meeting-minutes"
                      entityId={m._id}
                      attachments={m.attachments || []}
                      canEdit={canEdit}
                      onChange={(nextAtts) =>
                        setMinutes(prev => prev.map(x => x._id === m._id ? { ...x, attachments: nextAtts } : x))
                      }
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {editingMinute && (
        <EditMeetingMinuteModal
          minute={editingMinute}
          systemUsers={systemUsers}
          onClose={() => setEditingMinute(null)}
          onSaved={(updated) => {
            // Merge edited fields back into the list, preserving populated children
            // (goal, mission, discussionPoints.actionItems, attachments).
            setMinutes(prev => prev.map(x => x._id === updated._id ? {
              ...x,
              meetingDate: updated.meetingDate,
              topic: updated.topic,
              nextMeetingDate: updated.nextMeetingDate,
              notes: updated.notes,
              participants: updated.participants
            } : x));
            setEditingMinute(null);
          }}
        />
      )}
    </div>
  );
};

// ─── Edit Meeting Minute modal ──────────────────────────────────────────────
const EditMeetingMinuteModal = ({ minute, systemUsers = [], onClose, onSaved }) => {
  const [form, setForm] = useState({
    meetingDate:     minute.meetingDate ? String(minute.meetingDate).slice(0, 10) : '',
    topic:           minute.topic || '',
    nextMeetingDate: minute.nextMeetingDate ? String(minute.nextMeetingDate).slice(0, 10) : '',
    notes:           minute.notes || '',
    participants: (minute.participants || []).map(p => p.userId
      ? { userId: typeof p.userId === 'object' ? p.userId._id : p.userId }
      : { externalName: p.externalName }
    )
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.meetingDate) { alert('Meeting date is required.'); return; }
    setSaving(true);
    try {
      const { data } = await axiosInstance.put(`/api/vision/meeting-minutes/${minute._id}`, {
        meetingDate: form.meetingDate,
        topic: form.topic,
        nextMeetingDate: form.nextMeetingDate || null,
        notes: form.notes,
        participants: form.participants
      });
      onSaved(data.data);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.message || 'Failed to save meeting');
    } finally { setSaving(false); }
  };

  return (
    <div className="v26-drawer-overlay" onClick={onClose}>
      <div className="v26-drawer" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <div className="v26-drawer-header" style={{ position: 'relative' }}>
          <h2 style={{ margin: 0 }}>Edit Meeting</h2>
          <p style={{ marginTop: 4 }}>
            {minute.mission?.name || '—'} › {minute.goal?.name || '—'}
          </p>
          <button className="v26-drawer-close" onClick={onClose}>×</button>
        </div>
        <div className="v26-drawer-body">
          <div className="v26-form">
            <div className="v26-form-row">
              <div className="v26-form-group" style={{ flex: 1 }}>
                <label>Meeting Date *</label>
                <input type="date" value={form.meetingDate}
                  onChange={e => setForm(f => ({ ...f, meetingDate: e.target.value }))} />
              </div>
              <div className="v26-form-group" style={{ flex: 1 }}>
                <label>Next Meeting</label>
                <input type="date" value={form.nextMeetingDate}
                  onChange={e => setForm(f => ({ ...f, nextMeetingDate: e.target.value }))} />
              </div>
            </div>
            <div className="v26-form-group">
              <label>Meeting Title</label>
              <input type="text" value={form.topic}
                onChange={e => setForm(f => ({ ...f, topic: e.target.value }))}
                placeholder="e.g. Weekly leadership sync" />
            </div>
            <div className="v26-form-group">
              <label>Participants <span style={{ color: '#94a3b8', fontWeight: 400 }}>(optional, external allowed)</span></label>
              <AssigneePicker
                value={form.participants}
                onChange={(arr) => setForm(f => ({ ...f, participants: arr }))}
                systemUsers={systemUsers}
                placeholder="Add a participant…"
              />
            </div>
            <div className="v26-form-group">
              <label>Notes</label>
              <textarea value={form.notes} rows={3}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Any other notes..." />
            </div>
            <div style={{
              background: '#fef3c7', border: '1px solid #fcd34d', color: '#92400e',
              fontSize: 11, padding: '8px 10px', borderRadius: 6, marginBottom: 8
            }}>
              ⚠ Discussion points, agendas and action items can't be edited here. To change them,
              delete and re-create the meeting, or edit individual action items from the Roadmap.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="v26-btn v26-btn--secondary" onClick={onClose}>Cancel</button>
              <button className="v26-btn v26-btn--primary" onClick={save} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingMinutes;
